<template>
  <v-navigation-drawer
    v-model="drawer"
    app
    permanent
    color="white"
    :width="isMini ? 80 : 260"
    class="sidebar-drawer"
  >
    <!-- Toggle Button -->
    <v-btn
      icon
      size="small"
      variant="text"
      class="toggle-btn"
      @click="toggleMini"
    >
      <v-icon>{{ isMini ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
    </v-btn>

    <div class="logo-section pa-4 pr-8">
      <img 
        v-if="!isMini" 
        src="/images/logo-blue.svg" 
        alt="AquaFarm Pro" 
        class="sidebar-logo" 
      />
      <v-icon v-else size="40" color="primary">mdi-fish</v-icon>
      
      <!-- Company Name -->
      <div v-if="!isMini && companyName" class="company-name mt-2 text-center">
        <v-chip
          size="small"
          color="primary"
          variant="tonal"
          class="font-weight-medium"
        >
          {{ companyName }}
        </v-chip>
      </div>
    </div>

    <v-divider class="mx-4"></v-divider>

    <v-list density="compact" nav class="px-2 mt-2">
      <template v-if="isMini">
        <!-- Mini mode: Icon only with tooltip -->
        <v-list-item
          v-for="item in visibleMenuItems"
          :key="item.title"
          :to="item.to"
          class="mb-1 menu-item mini-item"
          :value="item.title"
        >
          <template v-slot:prepend>
            <v-icon :icon="item.icon" size="24"></v-icon>
          </template>
          
          <v-tooltip
            activator="parent"
            location="end"
          >
            {{ item.title }}
          </v-tooltip>
        </v-list-item>
      </template>
      
      <template v-else>
        <!-- Normal mode: Icon with text -->
        <v-list-item
          v-for="item in visibleMenuItems"
          :key="item.title"
          :to="item.to"
          rounded="lg"
          class="mb-1 menu-item"
        >
          <template v-slot:prepend>
            <v-icon>{{ item.icon }}</v-icon>
          </template>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>

    <template v-slot:append>
      <div class="pa-4">
        <v-btn
          :block="!isMini"
          :icon="isMini"
          color="primary"
          variant="tonal"
          :prepend-icon="!isMini ? 'mdi-logout' : undefined"
          @click="handleLogout"
          class="logout-btn"
        >
          <v-icon v-if="isMini">mdi-logout</v-icon>
          <span v-else>Logout</span>
          
          <v-tooltip
            v-if="isMini"
            activator="parent"
            location="end"
          >
            Logout
          </v-tooltip>
        </v-btn>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'

const router = useRouter()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()

const drawer = ref(true)
const isMini = ref(false)

const menuItems = [
  { title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/' },
  { title: 'Farms', icon: 'mdi-home-group', to: '/farms' },
  { title: 'Ponds', icon: 'mdi-pool', to: '/ponds' },
  { title: 'Fish Inventory', icon: 'mdi-fish', to: '/inventory' },
  { title: 'Water Quality', icon: 'mdi-water', to: '/water-quality' },
  { title: 'Feeding', icon: 'mdi-food', to: '/feeding' },
  { title: 'Feed Inventory', icon: 'mdi-package-variant', to: '/feed-inventory' },
  { title: 'Financial', icon: 'mdi-currency-usd', to: '/financial' },
  { title: 'Reports', icon: 'mdi-chart-line', to: '/reports' },
  { title: 'Company', icon: 'mdi-domain', to: '/company', roles: ['admin', 'manager'] },
  { title: 'Users', icon: 'mdi-account-multiple', to: '/users', roles: ['admin', 'manager'] },
  { title: 'Settings', icon: 'mdi-cog', to: '/settings', roles: ['admin'] }
]

const userRole = computed(() => authStore.user?.role)
const companyName = computed(() => authStore.company?.name)

const visibleMenuItems = computed(() => {
  return menuItems.filter(item => {
    if (!item.roles) return true
    if (!userRole.value) return false
    return item.roles.includes(userRole.value)
  })
})

function toggleMini() {
  isMini.value = !isMini.value
  localStorage.setItem('sidebarMini', String(isMini.value))
  
  // Emit event to notify parent about sidebar width change
  window.dispatchEvent(new CustomEvent('sidebar-toggle', { detail: { isMini: isMini.value } }))
}

async function handleLogout() {
  try {
    await authStore.logout()
    notificationStore.showSuccess('Logged out successfully')
    router.push('/login')
  } catch (error) {
    notificationStore.showError('Logout failed')
  }
}

// Load saved state from localStorage
onMounted(() => {
  const savedState = localStorage.getItem('sidebarMini')
  if (savedState !== null) {
    isMini.value = savedState === 'true'
  }
})
</script>

<style scoped>
.sidebar-drawer {
  border-right: 1px solid #e0e0e0 !important;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
  transition: width 0.3s ease;
}

.toggle-btn {
  position: absolute;
  top: 20px;
  right: -12px;
  z-index: 10;
  background: white !important;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-btn:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.logo-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 64px;
  transition: all 0.3s ease;
  position: relative;
}

.sidebar-logo {
  height: 42px;
  width: auto;
  transition: all 0.3s ease;
}

.company-name {
  font-size: 12px;
  line-height: 1.2;
  max-width: 200px;
  width: 100%;
}

.company-name .v-chip {
  max-width: 100%;
  height: auto !important;
  padding: 4px 8px;
}

.company-name .v-chip__content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  font-size: 11px;
}

.menu-item {
  color: #546e7a !important;
  margin: 2px 0;
  transition: all 0.2s ease;
}

.menu-item:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.mini-item {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  min-height: 48px !important;
  padding: 0 !important;
}

.mini-item :deep(.v-list-item__prepend) {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.mini-item :deep(.v-list-item__content) {
  display: none !important;
}

.mini-item :deep(.v-icon) {
  margin: 0 auto !important;
  opacity: 1 !important;
  font-size: 24px !important;
  color: #546e7a !important;
}

.mini-item:hover :deep(.v-icon) {
  color: #1976d2 !important;
}

.v-list-item--active {
  background-color: rgba(25, 118, 210, 0.12) !important;
  color: #1976d2 !important;
}

.v-list-item--active .v-icon {
  color: #1976d2 !important;
}

.logout-btn {
  text-transform: none;
  font-weight: 500;
  transition: all 0.2s ease;
}

/* Mini mode adjustments */
.v-navigation-drawer--rail .menu-item {
  justify-content: center;
}

.v-navigation-drawer--rail .logout-btn {
  min-width: 48px;
}
</style>